import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';

/**
 * Gate for protected tenant routes. Reads session status from context:
 *
 *   - `loading`        → show a minimal "checking session" placeholder
 *   - `authenticated`  → render children
 *   - `unauthenticated`→ redirect to /login, preserving return-to path
 *
 * The actual `GET /v1/auth/session` probe lives in `SessionProvider`;
 * this component does NOT fetch on its own, which keeps guard behavior
 * deterministic and avoids double-fetching across nested routes.
 */
interface Props {
  children: ReactNode;
}

export function RequireAuth({ children }: Props): JSX.Element {
  const { status } = useSession();
  const location = useLocation();
  const t = useT();

  if (status === 'loading') {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface px-8 text-sm text-outline"
        role="status"
        aria-live="polite"
      >
        <p>{t('common.session.checking')}</p>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
